import React, { FC, ReactNode } from 'react';
import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { Device } from '@thingslog/repositories';

const CommandsPageHeader: FC<CommandsPageHeaderProps> = ({
  deviceNumber,
  device,
  isAutoRefreshOn,
  onAutoRefreshChange,
  onRefresh,
  onSend,
  isRefreshing,
  isUpdating,
  freshness,
}: CommandsPageHeaderProps) => {
  const { t } = useTranslation();

  const matchingDevice = device && device.number === deviceNumber ? device : null;

  return (
    // `text-left` undoes CRA's `.App { text-align: center }` in App.css.
    <header className="flex flex-shrink-0 flex-col gap-2 text-left md:h-16 md:flex-row md:items-center md:justify-between md:gap-4 md:overflow-hidden">
      <div className="min-w-0">
        <Typography variant="h5" component="h1" className="font-semibold leading-tight">
          {t('commands_header')}
        </Typography>
        <Typography variant="body2" color="text.secondary" className="truncate">
          {matchingDevice?.name ? `${deviceNumber} · ${matchingDevice.name}` : deviceNumber}
        </Typography>
      </div>

      <div className="flex min-w-0 flex-shrink-0 items-center justify-between gap-3 md:justify-end md:gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <CircularProgress
            size={12}
            className={isUpdating ? 'visible' : 'invisible'}
            aria-hidden={!isUpdating}
          />
          {freshness && (
            <Typography
              variant="body2"
              color="text.secondary"
              role="status"
              className="truncate max-sm:hidden"
            >
              {freshness}
            </Typography>
          )}

          <FormControlLabel
            className="mx-0 min-h-[44px] md:min-h-0"
            control={
              <Switch
                size="small"
                checked={isAutoRefreshOn}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                  onAutoRefreshChange(event.target.checked)
                }
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {t('commands_auto_refresh')}
              </Typography>
            }
          />
        </div>

        <Divider orientation="vertical" className="h-6 self-center max-md:hidden" />

        <div className="flex flex-shrink-0 items-center gap-2">
          <Tooltip title={t('commands_btn_refresh')} arrow>
            <span>
              <Button
                variant="outlined"
                onClick={onRefresh}
                disabled={isRefreshing}
                aria-label={t('commands_btn_refresh')}
                className="min-h-[44px] min-w-[44px] gap-2 px-3 md:min-h-0 md:min-w-0 md:px-4"
              >
                <CachedOutlinedIcon fontSize="small" />
                <span className="max-md:hidden">{t('commands_btn_refresh')}</span>
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            onClick={onSend}
            aria-label={t('commands_btn_send_command')}
            className="min-h-[44px] min-w-[44px] gap-2 px-3 md:min-h-0 md:min-w-0 md:px-4"
          >
            <AddIcon fontSize="small" />
            <span className="max-md:hidden">{t('commands_btn_send_command')}</span>
          </Button>
        </div>
      </div>
    </header>
  );
};

interface CommandsPageHeaderProps {
  deviceNumber: string;
  device: Device | null;
  isAutoRefreshOn: boolean;
  onAutoRefreshChange: (next: boolean) => void;
  onRefresh: () => void;
  onSend: () => void;
  isRefreshing: boolean;
  isUpdating: boolean;
  freshness: ReactNode;
}

export default CommandsPageHeader;
